// Replies typed on a claim's card over the Lanes view, and what the chat said
// back, so the answer shows where the reader asked.
//
// A reply is the reader's own message in the chat that owns the graph:
// ChatConsole sends it there and it stays in that chat's transcript, tagged
// with the reply's id and the claims it was asked about. This store carries its
// progress back to the card -- waiting for the chat, working, the answer, or
// why nothing came -- keyed by the graph the card reads. One store per page.

const EMPTY = Object.freeze([]);

const FIELDS = ["state", "activity", "tools", "answer", "error", "text", "at"];

// A thread reads in the order it was asked, whatever order its replies reach
// the store in: after a reload the reader's new question arrives first and the
// transcript's earlier ones only once the chat's history has loaded.
const asked = (a, b) => (a.at ?? Infinity) - (b.at ?? Infinity);

export function createReplyStore() {
    const byKey = new Map();
    const listeners = new Map();
    const watchers = new Set();
    const notify = (key) => {
        for (const cb of listeners.get(key) || []) {
            try { cb(); } catch { /* a listener must not break the chat */ }
        }
    };
    const notifyWatch = () => {
        for (const cb of watchers) {
            try { cb(); } catch { /* a watcher must not break a card */ }
        }
    };
    return {
        list(graphKey) {
            return byKey.get(String(graphKey)) || EMPTY;
        },
        upsert(graphKey, reply) {
            if (graphKey == null || !reply?.id) return;
            const key = String(graphKey);
            const cur = byKey.get(key) || EMPTY;
            const i = cur.findIndex((r) => r.id === reply.id);
            const prev = i >= 0 ? cur[i] : null;
            const next = prev ? { ...prev, ...reply } : reply;
            if (prev && FIELDS.every((f) => prev[f] === next[f])) return;
            const list = i >= 0 ? cur.map((r, j) => (j === i ? next : r)) : [...cur, next];
            byKey.set(key, list.sort(asked));
            notify(key);
        },
        subscribe(graphKey, cb) {
            const key = String(graphKey);
            if (!listeners.has(key)) listeners.set(key, new Set());
            const set = listeners.get(key);
            const first = set.size === 0;
            set.add(cb);
            if (first) notifyWatch();
            return () => {
                set.delete(cb);
                if (!set.size) {
                    listeners.delete(key);
                    notifyWatch();
                }
            };
        },
        // The graphs a card is reading right now, so the chat that owns one
        // can bring its earlier replies in before the reader asks anything.
        watchedKeys() {
            return [...listeners.keys()];
        },
        onWatch(cb) {
            watchers.add(cb);
            return () => { watchers.delete(cb); };
        },
    };
}

export const graphReplies = createReplyStore();

// A reply's id: unique in the page, and in the chat's transcript it is kept in.
let replySeq = 0;
export function nextReplyId(now = Date.now()) {
    replySeq += 1;
    return `r${now.toString(36)}${replySeq.toString(36)}`;
}

const LIVE_TOOL = new Set(["preparing", "queued", "running"]);
const toolLabel = (name) => String(name || "a tool").replace(/^mcp__[^_]+__/, "").replace(/_/g, " ");

/**
 * What a chat's transcript says about one reply, or null before the reply is
 * in it: the turns after the reader's message, up to the next message they
 * typed themselves. While the chat runs, what it is doing and what it has said
 * so far; once it stops, the last thing it said, which is its answer.
 */
export function replyProgress(chat, replyId) {
    const items = chat?.items || [];
    const at = items.findIndex((it) => it?.kind === "user" && it.graphReply?.id === replyId);
    if (at < 0) return null;
    const turns = [];
    let end = at + 1;
    for (; end < items.length; end += 1) {
        const it = items[end];
        if (it?.kind === "user" && !it.queued) break;
        if (it?.kind === "turn") turns.push(it);
    }
    const running = end >= items.length && Boolean(chat.busy);
    const children = turns.flatMap((t) => t.children || []);
    const tools = children.filter((c) => c?.kind === "tool").length;
    const said = turns
        .map((t) => (t.children || []).filter((c) => c?.kind === "assistant").map((c) => c.text || "").join("").trim())
        .filter(Boolean);
    const answer = said.length ? said[said.length - 1] : "";
    if (running) {
        const live = [...children].reverse().find((c) => c?.kind === "tool" && LIVE_TOOL.has(c.status));
        const last = children[children.length - 1];
        const activity = live
            ? `running ${toolLabel(live.name)}`
            : last?.kind === "thinking" ? "thinking" : turns.length ? "writing" : "starting";
        return { state: "working", activity, tools, answer };
    }
    const lastTurn = turns[turns.length - 1];
    if (lastTurn?.stopReason === "error") return { state: "failed", activity: null, tools, answer, error: chat.error || null };
    if (lastTurn?.stopReason === "aborted" && !answer) return { state: "stopped", activity: null, tools, answer };
    if (!turns.length && chat.error) return { state: "failed", activity: null, tools, answer, error: chat.error };
    return { state: "answered", activity: null, tools, answer };
}

// Hand a reply typed on a card to the chat that owns the graph. The chat
// console's `origin-chat-reply` listener takes it synchronously and names the
// reply it queued; with no chat to take it the result says "missing".
export function sendGraphReply(graphKey, { text, words, claimIds } = {}, target = globalThis.window) {
    if (!graphKey || !text || typeof target?.dispatchEvent !== "function") return null;
    const detail = { graphKey, text, words, claimIds };
    target.dispatchEvent(new CustomEvent("origin-chat-reply", { detail }));
    return { replyId: detail.replyId || null, result: detail.result || "missing" };
}

// Bring the chat that owns a graph forward: the page flips its column to the
// chat, and the console picks that chat (`origin-chat-activate`).
export function activateGraphChat(graphKey, target = globalThis.window) {
    if (!graphKey || typeof target?.dispatchEvent !== "function") return;
    target.dispatchEvent(new CustomEvent("origin-chat-activate", { detail: { graphKey } }));
}
